// The Auto-Grouping Engine (Phase 1) — the "Remainder of 4" rule.
//
// Groups of 4 are prioritized. The leftover players (totalPlayers % 4) are
// absorbed by mixing in a group of 5 or groups of 3:
//
//   remainder 0 -> all groups of 4
//   remainder 1 -> all groups of 4, except one group of 5
//   remainder 2 -> all groups of 4, except two groups of 3 (default)
//   remainder 3 -> all groups of 4, except one group of 3
//
// The default for remainder 2 is two groups of 3; the spec also allows two
// groups of 5, exposed via the `preferLargerOnRemainderTwo` option.

import { PLAYER_MAX, PLAYER_MIN } from './types';

export interface GroupingOptions {
  /** When true, remainder 2 resolves to two groups of 5 instead of two of 3. */
  preferLargerOnRemainderTwo?: boolean;
}

/** A source of randomness in [0, 1), matching the `Math.random` contract. */
export type RandomSource = () => number;

/**
 * Reorders 1-based player indices before they are split into groups. Injecting
 * a strategy keeps `assignPlayersToGroups` pure and deterministic in tests; the
 * default strategy is a random shuffle.
 */
export type OrderStrategy = (indices: readonly number[]) => number[];

/**
 * Pure Fisher-Yates shuffle: returns a new array with the same members in a
 * random order. The input is never mutated. `random` is injectable so callers
 * (and tests) can supply a deterministic source.
 */
export function shuffleIndices(
  indices: readonly number[],
  random: RandomSource = Math.random,
): number[] {
  const result = [...indices];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    const temp = result[i];
    result[i] = result[j];
    result[j] = temp;
  }
  return result;
}

/**
 * Returns the list of group sizes for `totalPlayers`, largest-or-standard
 * groups first. Throws if `totalPlayers` is outside the supported
 * `PLAYER_MIN..PLAYER_MAX` range.
 */
export function computeGroupSizes(
  totalPlayers: number,
  options: GroupingOptions = {},
): number[] {
  if (!Number.isInteger(totalPlayers)) {
    throw new Error(`totalPlayers must be an integer, received ${totalPlayers}`);
  }
  if (totalPlayers < PLAYER_MIN || totalPlayers > PLAYER_MAX) {
    throw new Error(
      `totalPlayers must be between ${PLAYER_MIN} and ${PLAYER_MAX}, received ${totalPlayers}`,
    );
  }

  const groupsOfFour = Math.floor(totalPlayers / 4);
  const remainder = totalPlayers % 4;

  let sizes: number[];
  switch (remainder) {
    case 0:
      sizes = fill(4, groupsOfFour);
      break;
    case 1:
      // Turn one group of 4 into a group of 5.
      sizes = [...fill(4, groupsOfFour - 1), 5];
      break;
    case 2:
      // Two groups of 5 consume two groups of 4 plus the remainder (needs at
      // least two groups of 4); otherwise fall back to two groups of 3.
      sizes =
        options.preferLargerOnRemainderTwo && groupsOfFour >= 2
          ? [...fill(4, groupsOfFour - 2), 5, 5]
          : [...fill(4, groupsOfFour - 1), 3, 3];
      break;
    case 3:
      sizes = [...fill(4, groupsOfFour), 3];
      break;
    default:
      // Unreachable: remainder is always 0..3.
      throw new Error(`unexpected remainder ${remainder}`);
  }

  // Order groups largest-first for a stable, readable layout.
  return sizes.sort((a, b) => b - a);
}

/**
 * Splits the player indices (1..totalPlayers) into groups according to
 * `computeGroupSizes`. The indices are reordered by `order` before slicing, so
 * by default players are spread across groups in random order rather than in
 * roster order. Returns an array of arrays of 1-based player indices.
 */
export function assignPlayersToGroups(
  totalPlayers: number,
  options: GroupingOptions = {},
  order: OrderStrategy = shuffleIndices,
): number[][] {
  const sizes = computeGroupSizes(totalPlayers, options);
  const ordered = order(sequentialIndices(totalPlayers));
  if (ordered.length !== totalPlayers) {
    throw new Error(
      `order strategy must return ${totalPlayers} indices, received ${ordered.length}`,
    );
  }

  const groups: number[][] = [];
  let cursor = 0;
  for (const size of sizes) {
    groups.push(ordered.slice(cursor, cursor + size));
    cursor += size;
  }
  return groups;
}

/** Returns the 1-based player indices [1, 2, ..., totalPlayers]. */
function sequentialIndices(totalPlayers: number): number[] {
  return Array.from({ length: totalPlayers }, (_, i) => i + 1);
}

function fill(value: number, count: number): number[] {
  return count > 0 ? new Array<number>(count).fill(value) : [];
}
